import React, { useState } from 'react';

import {
  Stack,
  Text,
  Button,
  Group,
  Alert,
  LoadingOverlay,
  Radio,
  Card,
  Title
} from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { type Client } from './types';
import { PostJSON, usePostJSON } from '../../utils/request';

interface AssignAgentProps {
  client: Client;
  onSuccess: () => void;
  onCancel: () => void;
}

interface PhoneNumber {
  sid: string;
  phoneNumber: string;
  friendlyName: string;
}

interface PhoneNumbersResponse {
  success: boolean;
  phoneNumbers: PhoneNumber[];
  count: number;
}

const AssignAgent: React.FC<AssignAgentProps> = ({ client, onSuccess, onCancel }) => {
  const [selectedPhoneSid, setSelectedPhoneSid] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Hook for phone numbers
  const {
    data: phoneNumbersData,
    error: phoneNumbersError,
    loading: phoneNumbersLoading
  } = usePostJSON<PhoneNumbersResponse>('/api/agent/list_numbers');

  const handleAddAgent = async () => {
    if (!selectedPhoneSid) {
      notifications.show({
        title: 'Error',
        message: 'Please select a phone number',
        color: 'red'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await PostJSON('/api/agent/add', {
        clientId: client.id,
        sid: selectedPhoneSid
      });
      
      notifications.show({
        title: 'Success',
        message: `Phono Assistant has been successfully added to ${client.firstName} ${client.lastName}`,
        color: 'green'
      });
      
      onSuccess();
    } catch (error) {
      console.error('Error adding agent:', error);
      
      notifications.show({
        title: 'Error',
        message: 'Failed to add Phono Assistant. Please try again.',
        color: 'red'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (phoneNumbersError) {
    return (
      <Alert color="red" title="Error">
        Failed to load phone numbers. Please try again.
      </Alert>
    );
  }

  const availablePhoneNumbers = phoneNumbersData?.phoneNumbers || [];

  return (
    <Stack gap="md" style={{ position: 'relative' }}>
      <LoadingOverlay visible={phoneNumbersLoading || isSubmitting} />

      <Card withBorder>
        <Title mb="sm" order={4}>Client Information</Title>
        <Stack gap="xs">
          <Text><strong>Name:</strong> {client.firstName} {client.lastName}</Text>
          {client.dob && (
            <Text><strong>Date of Birth:</strong> {new Date(client.dob).toLocaleDateString()}</Text>
          )}
        </Stack>
      </Card>

      <Radio.Group
        label="Available Phone Numbers"
        value={selectedPhoneSid}
        onChange={setSelectedPhoneSid}
      >
        <Stack gap="sm" mt="xs">
          {availablePhoneNumbers.map((phoneNumber) => (
            <Radio
              key={phoneNumber.sid}
              disabled={phoneNumbersLoading || isSubmitting}
              label={
                <div>
                  <b>{phoneNumber.phoneNumber}</b>
                  {phoneNumber.friendlyName && (
                    <Text c="dimmed" component="span" ml="xs" size="sm">
                      ({phoneNumber.friendlyName})
                    </Text>
                  )}
                </div>
              }
              value={phoneNumber.sid}
            />
          ))}
        </Stack>
      </Radio.Group>

      {availablePhoneNumbers.length === 0 && !phoneNumbersLoading && (
        <Alert color="yellow" title="No Phone Numbers Available">
          There are no phone numbers available to assign as Phono Assistants. Please contact support.
        </Alert>
      )}

      <Group gap="sm" justify="flex-end" mt="md">
        <Button 
          disabled={isSubmitting}
          variant="outline" 
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button 
          disabled={!selectedPhoneSid || isSubmitting}
          loading={isSubmitting}
          onClick={() => void handleAddAgent()}
        >
          Add Phono Assistant
        </Button>
      </Group>
    </Stack>
  );
};

export default AssignAgent;
